import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { Search, Plus, Edit2, Trash2, Layers, X, Briefcase, Video, Image as ImageIcon } from "lucide-react";
import Swal from 'sweetalert2';
import { API_BASE_URL } from "../config";

export default function PoliticsManagement() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingPoliticsId, setEditingPoliticsId] = useState(null);
    const [newPolitics, setNewPolitics] = useState({ name: "", image: "", video: "" });
    const [selectedImages, setSelectedImages] = useState([]);
    const [selectedVideos, setSelectedVideos] = useState([]);
    const [previewImage, setPreviewImage] = useState(null);
    const [politicsEntries, setPoliticses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [politicsNamesList, setPoliticsNamesList] = useState([]);

    const fetchPoliticsNames = () => {
        fetch(`${API_BASE_URL}/api/politics-names/all`)
            .then(res => res.json())
            .then(resJson => {
                if (resJson && resJson.status && resJson.data) {
                    setPoliticsNamesList(resJson.data);
                }
            })
            .catch(err => console.error(err));
    };

    const fetchPoliticses = () => {
        setLoading(true);
        fetch(`${API_BASE_URL}/api/politics/all-politics`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch politicsEntries");
                return res.json();
            })
            .then((resJson) => {
                if (resJson && resJson.status && resJson.data) {
                    setPoliticses(resJson.data);
                } else {
                    throw new Error(resJson.message || "Invalid response format");
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Politicses fetch error:", err);
                setError(err.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchPoliticses();
        fetchPoliticsNames();
    }, []);

    const handleSavePolitics = () => {
        if (!newPolitics.name.trim() && selectedImages.length === 0 && selectedVideos.length === 0) {
            Swal.fire("Error", "Please provide a name, image, or video!", "error");
            return;
        }

        const formData = new FormData();
        formData.append("politics_name", newPolitics.name);
        selectedImages.forEach(file => formData.append("politics_image", file));
        selectedVideos.forEach(file => formData.append("politics_video", file));

        Swal.fire({
            title: editingPoliticsId ? "Updating..." : "Creating...",
            text: "Please wait.",
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        const url = editingPoliticsId
            ? `${API_BASE_URL}/api/politics/update-politics/${editingPoliticsId}`
            : `${API_BASE_URL}/api/politics/create-politics`;
        const method = editingPoliticsId ? "PUT" : "POST";

        fetch(url, { method, body: formData })
            .then(res => {
                if (!res.ok) throw new Error("Failed to save politics");
                return res.json();
            })
            .then(resJson => {
                Swal.close();
                if (resJson && resJson.status) {
                    Swal.fire("Success", `Politics ${editingPoliticsId ? 'updated' : 'created'} successfully!`, "success");
                    fetchPoliticses();
                    resetForm();
                } else {
                    throw new Error(resJson.message || "Failed to save politics");
                }
            })
            .catch(err => {
                Swal.close();
                console.error("Save politics error:", err);
                Swal.fire("Error", err.message, "error");
            });
    };

    const resetForm = () => {
        setNewPolitics({ name: "", image: "", video: "" });
        setSelectedImages([]);
        setSelectedVideos([]);
        setEditingPoliticsId(null);
        setIsCreateModalOpen(false);
    };

    const handleEditPolitics = (politics) => {
        setNewPolitics({ name: politics.politics_name || "", image: politics.politics_image || "", video: politics.politics_video || "" });
        setEditingPoliticsId(politics.politics_id_PK);
        setSelectedImages([]);
        setSelectedVideos([]);
        setIsCreateModalOpen(true);
    };

    const handleDeletePolitics = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({ title: "Deleting...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                fetch(`${API_BASE_URL}/api/politics/delete-politics/${id}`, { method: "DELETE" })
                    .then(res => {
                        if (!res.ok) throw new Error("Failed to delete politics");
                        return res.json();
                    })
                    .then(resJson => {
                        Swal.close();
                        if (resJson && resJson.status) {
                            Swal.fire('Deleted!', 'Politics has been deleted.', 'success');
                            fetchPoliticses();
                        } else {
                            throw new Error(resJson.message || "Failed to delete politics");
                        }
                    })
                    .catch(err => {
                        Swal.close();
                        console.error("Delete error:", err);
                        Swal.fire("Error", err.message, "error");
                    });
            }
        });
    };

    return (
        <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
                <Header
                    onMenuClick={() => setIsSidebarOpen(true)}
                    title="Politics Management"
                    subtitle="Manage Politics images and videos."
                />

                <div className="p-4 sm:p-8 flex-1 flex flex-col w-full overflow-hidden">
                    <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="relative w-full sm:w-72">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                placeholder="Search politicsEntries..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white dark:bg-slate-800/80 backdrop-blur-md border border-white dark:border-slate-700 shadow-[0_4px_20px_rgba(0,0,0,0.03)] rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-blue-500 transition-all dark:text-slate-100"
                            />
                        </div>

                        <div className="flex items-center gap-3 w-full lg:w-auto">
                            <button
                                onClick={() => { resetForm(); setIsCreateModalOpen(true); }}
                                className="flex-1 lg:flex-none justify-center bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2">
                                <Plus size={16} />
                                Create Politics Entry
                            </button>
                        </div>
                    </div>

                    {error && <div className="mb-6 p-4 bg-rose-50 border border-rose-100 text-rose-600 rounded-2xl">{error}</div>}

                    {loading ? (
                        <div className="flex items-center justify-center py-12 flex-1 animate-pulse">Loading...</div>
                    ) : politicsEntries.length === 0 ? (
                        <div className="flex items-center justify-center py-12 flex-1">No politicsEntries found.</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            {politicsEntries.filter(b => (b.politics_name || '').toLowerCase().includes(searchTerm.toLowerCase())).map(politics => (
                                <div key={politics.politics_id_PK} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border p-6 flex flex-col">
                                    <h3 className="text-lg font-bold mb-4">{politics.politics_name || "Untitled"}</h3>

                                    <div className="flex gap-2 max-w-full overflow-x-auto pb-2">
                                        {politics.politics_image && politics.politics_image.split(',').map((img, i) => (
                                            <img
                                                key={`img-${i}`}
                                                src={img}
                                                alt="politics"
                                                className="w-16 h-16 rounded-xl object-cover border cursor-pointer hover:opacity-80 transition-opacity"
                                                onClick={() => setPreviewImage(img)}
                                            />
                                        ))}
                                        {politics.politics_video && politics.politics_video.split(',').map((vid, i) => (
                                            <video key={`vid-${i}`} src={vid} className="w-16 h-16 rounded-xl object-cover border bg-black" controls />
                                        ))}
                                    </div>

                                    <div className="mt-auto flex gap-2 pt-4 border-t">
                                        <button onClick={() => handleEditPolitics(politics)} className="flex-1 flex gap-1 justify-center py-2 text-sm text-slate-600 hover:text-blue-600 bg-slate-50 rounded-lg">
                                            <Edit2 size={16} /> Edit
                                        </button>
                                        <button onClick={() => handleDeletePolitics(politics.politics_id_PK)} className="flex-1 flex gap-1 justify-center py-2 text-sm text-rose-600 hover:text-rose-700 bg-rose-50 rounded-lg">
                                            <Trash2 size={16} /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {isCreateModalOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setIsCreateModalOpen(false)}></div>
                    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 w-full max-w-md relative z-10 shadow-xl overflow-y-auto max-h-[90vh]">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-lg font-bold">{editingPoliticsId ? 'Edit Politics Entry' : 'Create Politics Entry'}</h3>
                            <button onClick={() => setIsCreateModalOpen(false)}><X size={20} /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1.5">Politics Name</label>
                                <select
                                    value={newPolitics.name}
                                    onChange={e => setNewPolitics({ ...newPolitics, name: e.target.value })}
                                    className="w-full bg-slate-50 border rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500"
                                >
                                    <option value="">Select a politics category</option>
                                    {politicsNamesList.map((item) => (
                                        <option key={item.id} value={item.name}>
                                            {item.name}
                                        </option>
                                    ))}
                                    {/* Fallback to allow existing names that might not be in the table to be shown when editing */}
                                    {newPolitics.name && !politicsNamesList.find(b => b.name === newPolitics.name) && (
                                        <option value={newPolitics.name}>{newPolitics.name}</option>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1.5">Images</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={e => setSelectedImages(Array.from(e.target.files))}
                                    className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-blue-50 file:text-blue-600"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1.5">Videos</label>
                                <input
                                    type="file"
                                    accept="video/*"
                                    multiple
                                    onChange={e => setSelectedVideos(Array.from(e.target.files))}
                                    className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:bg-rose-50 file:text-rose-600"
                                />
                            </div>
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button onClick={() => setIsCreateModalOpen(false)} className="px-4 py-2 bg-slate-100 rounded-xl">Cancel</button>
                            <button onClick={handleSavePolitics} className="px-4 py-2 bg-blue-600 text-white rounded-xl">Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Preview Modal */}
            {previewImage && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm" onClick={() => setPreviewImage(null)}>
                    <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
                        <button
                            onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}
                            className="absolute -top-4 -right-4 p-2 bg-white text-slate-800 rounded-full hover:bg-slate-200 transition-colors shadow-lg z-10"
                        >
                            <X size={20} />
                        </button>
                        <img
                            src={previewImage}
                            alt="Preview"
                            className="w-auto h-auto max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
